import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../utils/api';
import { toast } from 'sonner';

export default function BillingSuccessPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const { isDark } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const status = await api.getCheckoutStatus(sessionId);
      setPaymentStatus(status);
      
      if (status.payment_status === 'paid') {
        toast.success('Pagamento confirmado! Seu plano foi atualizado.');
      }
    } catch (error) {
      toast.error('Erro ao verificar status do pagamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto py-16 text-center" data-testid="billing-success-page">
        {loading ? (
          <div className="flex flex-col items-center">
            <Loader2 className="h-12 w-12 text-primary animate-spin mb-4" />
            <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>
              Verificando pagamento...
            </p>
          </div>
        ) : paymentStatus?.payment_status === 'paid' ? (
          <>
            <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-primary" />
            </div>
            
            <h1 className={`text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>
              Pagamento Confirmado!
            </h1>
            
            <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'} mb-8`}>
              Seu plano foi atualizado com sucesso. Agora você tem acesso a todas as funcionalidades do seu novo plano.
            </p>
            
            <div className={`${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'} rounded-xl border p-6 mb-8`}>
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Valor</p>
                  <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    R$ {(paymentStatus.amount_total / 100).toFixed(2).replace('.', ',')}
                  </p>
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Status</p>
                  <p className="text-lg font-semibold text-primary">
                    Pago
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button
                onClick={() => navigate('/dashboard')}
                className="bg-primary hover:bg-primary-hover"
              >
                Ir para o Dashboard
              </Button>
              <Button
                onClick={() => navigate('/billing')}
                variant="outline"
                className={isDark ? 'border-slate-700 text-slate-300 hover:bg-slate-800' : ''}
              >
                Ver meu plano
              </Button>
            </div>
          </>
        ) : (
          <>
            <h1 className={`text-3xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'} mb-4`}>
              Verificando Pagamento
            </h1>
            
            <p className={`text-lg ${isDark ? 'text-slate-400' : 'text-slate-600'} mb-8`}>
              Seu pagamento está sendo processado. Isso pode levar alguns minutos.
            </p>
            
            <Button
              onClick={() => navigate('/billing')}
              className="bg-primary hover:bg-primary-hover"
            >
              Ver Planos
            </Button>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
